import React from 'react'

import { Link, Stack, Typography, useTheme } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import { InlineEditButton } from '@repo/ui'
// VRG-273 aliased `@repo/types` to source in the platform's vite config, so this is
// a RUNTIME import, not a type-only one. It has to be: the whole point of AC-2 is
// that the platform and the server answer "when does this activate" with the SAME
// function, not with two implementations that agree until one is edited.
import {
  ActivationTriggerSource,
  resolveActivationTrigger,
  type ActivationTriggerValue,
} from '@repo/types'

import { ACTIVATION_TRIGGERS } from 'constants/activationTriggers'
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'

import { InfoItem } from '../InfoBlock'
import { PermissionGate } from '../../access/PermissionGate'

export type ActivationTriggerScope = 'product' | 'certificate'

interface ActivationTriggerItemProps {
  /** This record's own override. `null` = inherit. */
  own: ActivationTriggerValue | null | undefined
  /** The product's override — only meaningful for `scope="certificate"`. */
  product?: ActivationTriggerValue | null
  /** The company's Settings -> Features default. */
  company: ActivationTriggerValue | null | undefined
  scope: ActivationTriggerScope
  /** Needed to link "From the product" at certificate scope. */
  productId?: string
  /** `undefined` while loading — `InfoItem` renders its own skeleton for that. */
  loading?: boolean
  onEdit: () => void
}

/**
 * VRG-528 — the activation-trigger row, shared by the product and certificate
 * detail pages.
 *
 * Extracted BEFORE the second hand-roll rather than after (§36): both pages need
 * the identical "resolved value + where it came from + edit affordance" cell, and
 * the source-attribution logic is the part most likely to drift if written twice.
 *
 * Shows the SOURCE, not just the value, because "On sale" alone does not tell an
 * operator whether they are looking at a deliberate choice for this record or at
 * the company default leaking through — and those call for different actions. It
 * is also what makes AC-6 legible: clearing an override has to visibly return the
 * row to the inherited value rather than to a blank.
 */
export const ActivationTriggerItem = ({
  own,
  product,
  company,
  scope,
  productId,
  loading,
  onEdit,
}: ActivationTriggerItemProps) => {
  const theme = useTheme()
  // Carries "← Back to …" so a jump to Features or the product can return (§42).
  const referrer = useReferrerState()

  const resolved = resolveActivationTrigger(
    scope === 'certificate'
      ? { certificate: own, product, company }
      : { product: own, company },
  )

  // One lookup, because the title and the glyph are two properties of the SAME
  // option and reading them separately is how they drift.
  const option = ACTIVATION_TRIGGERS.find(o => o.id === resolved.trigger)
  const title = option?.title ?? resolved.trigger
  const TriggerIcon = option?.icon

  /**
   * Where the value came from, in the reader's terms — and a way to GET there.
   *
   * `Platform default` used to be its own string, distinct from `Company default`,
   * on the grounds that one means "nobody ever chose" and the other means "someone
   * did". That distinction is real but it is an engineer's: functionally the company
   * default is what applies either way, and the question an operator actually has is
   * "where do I change this". So both now read `Company default` and both link to
   * Settings -> Features, which answers it in one click.
   *
   * The product source links to the product for the same reason — the override
   * lives there, and a label that names a place you cannot navigate to is a dead end.
   */
  const sourceLink: Partial<Record<ActivationTriggerSource, string>> = {
    [ActivationTriggerSource.Company]: ROUTES.SETTINGS_COMPANY_FEATURES,
    [ActivationTriggerSource.Default]: ROUTES.SETTINGS_COMPANY_FEATURES,
    ...(scope === 'certificate' && productId
      ? {[ActivationTriggerSource.Product]: ROUTES.PRODUCT(productId)}
      : {}),
  }

  const sourceLabel: Record<ActivationTriggerSource, string> = {
    [ActivationTriggerSource.Certificate]: 'Set on this certificate',
    [ActivationTriggerSource.Product]:
      scope === 'product' ? 'Set on this product' : 'From the product',
    [ActivationTriggerSource.Company]: 'Company default',
    [ActivationTriggerSource.Default]: 'Company default',
  }

  return (
    <InfoItem
      label="Activation trigger"
      term="activation-trigger"
      value={
        loading ? undefined : (
          /*
            ONE LINE: "[icon] On sale · Company default".

            The value and its source used to be stacked, which put two lines of type
            into a row sized for one and read as cramped, and it implied the source
            was a fact of equal standing rather than an aside about where the value
            came from. Inline with a middot, it reads the way you would say it out
            loud, and the row keeps the single-line rhythm of the InfoItems beside
            it. The parentheses that briefly replaced the stack are gone: a middot
            already says "related but subordinate", and brackets around a link make
            it look like an aside you are not meant to click.

            THE AC-5 ACTIVATED DOT IS DELIBERATELY GONE, not mislaid. It was a
            coloured dot sitting exactly where a separator belongs, so the row had
            two dots meaning two different things a few pixels apart. It also had
            nothing left to tell anyone: it only ever rendered on a certificate
            (`activated` is undefined on a product), and on that page the status
            pill in the header and the "Activation date" field directly above both
            state the same fact in words. Three indicators of one fact, and the
            least legible of them was the one colliding with the punctuation.
          */
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flexWrap: 'wrap', rowGap: 1 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {/*
                The trigger's own glyph, from ACTIVATION_TRIGGERS — the same one the
                picker and the Features cards draw, so the three surfaces name the
                same rule with the same symbol.
              */}
              {TriggerIcon && (
                <TriggerIcon
                  size={16}
                  strokeWidth={1}
                  color={theme.palette.text.secondary}
                  style={{ flexShrink: 0 }}
                />
              )}
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ lineHeight: 1.25 }}
              >
                {title}
              </Typography>
              <Typography
                component="span"
                aria-hidden
                variant="body3"
                color="text.secondary"
                sx={{ lineHeight: 1.25 }}
              >
                ·
              </Typography>
              {sourceLink[resolved.source] ? (
                <Link
                  component={RouterLink}
                  to={sourceLink[resolved.source] as string}
                  state={referrer}
                  variant="body3"
                  color="text.secondary"
                  // Underline on hover only. At rest the source is context, and a
                  // permanent underline gave a secondary aside the loudest
                  // affordance in the row; on hover it still says "clickable".
                  sx={{
                    lineHeight: 1.25,
                    textDecoration: 'none',
                    textDecorationColor: 'inherit',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {sourceLabel[resolved.source]}
                </Link>
              ) : (
                <Typography
                  variant="body3"
                  color="text.secondary"
                  sx={{lineHeight: 1.25}}
                >
                  {sourceLabel[resolved.source]}
                </Typography>
              )}
            </Stack>
            {/*
              Gated on `billing_activation_settings`, the same permission the API
              routes carry at all three scopes — not on manage_product /
              manage_certificate, which every role that can edit a title holds.
              `PermissionGate`'s default `disable` mode shows it with a tooltip
              naming the permission rather than hiding it (§27/§46).
            */}
            <PermissionGate permission="billing_activation_settings">
              <InlineEditButton
                label="Edit"
                onClick={onEdit}
                aria-label={`Edit activation trigger for this ${scope}`}
              />
            </PermissionGate>
          </Stack>
        )
      }
    />
  )
}
